import type { FileNode } from "@adafia/utils";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
	ChevronDown,
	ChevronRight,
	FileText,
	Folder,
	FolderPlus,
	Globe,
	Pencil,
	Plus,
	Trash2,
} from "lucide-react";
import type React from "react";
import {
	memo,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { DragHandle } from "./DragHandle";
import "./TreeView.css";
import { useCreateNode } from "@/notes/hooks/useCreateNode";
import { TreeViewContext } from "./TreeViewContext";

interface TreeNodeProps {
	node: FileNode;
}

interface NodeEditInputProps {
	node: FileNode;
	tempTitle: string;
	setTempTitle: (title: string) => void;
	handleKeyDown: (e: React.KeyboardEvent) => void;
}

const NodeEditInput: React.FC<NodeEditInputProps> = ({
	node,
	tempTitle,
	setTempTitle,
	handleKeyDown,
}) => {
	return (
		<div className="node-content-edit-container">
			{node.isFolder ? (
				<Folder size={16} className="folder-icon" />
			) : (
				<FileText size={16} className="file-icon" />
			)}
			<input
				className="edit-input"
				// biome-ignore lint/a11y/noAutofocus: Autofocus is needed here for UX when renaming
				autoFocus
				onFocus={(e) => e.target.select()}
				value={tempTitle}
				onChange={(e) => setTempTitle(e.target.value)}
				onKeyDown={handleKeyDown}
				placeholder="Enter title..."
				onClick={(e) => e.stopPropagation()}
			/>
		</div>
	);
};

interface NodeStaticContentProps {
	node: FileNode;
	isActive: boolean;
	handleNodeClick: (e: React.MouseEvent) => void;
	setRenamingId: (id: string | null) => void;
}

const NodeStaticContent: React.FC<NodeStaticContentProps> = ({
	node,
	isActive,
	handleNodeClick,
	setRenamingId,
}) => {
	return (
		<button
			type="button"
			className="node-content-button"
			onClick={handleNodeClick}
			onDoubleClick={() => setRenamingId(node.id)}
			aria-selected={isActive}
			role="treeitem"
		>
			{node.isFolder ? (
				<Folder size={16} className="folder-icon" />
			) : (
				<FileText size={16} className="file-icon" />
			)}
			<span className="node-name" title={node.title}>
				{node.title}
				{node.isPublished && (
					<span title="Published">
						<Globe
							size={10}
							style={{
								marginLeft: "0.5rem",
								color: "var(--color-accent)",
							}}
						/>
					</span>
				)}
			</span>
		</button>
	);
};

export const TreeNode: React.FC<TreeNodeProps> = memo(({ node }) => {
	const {
		activeNote,
		renamingId,
		expandedIds,
		toggleExpanded,
		setRenamingId,
		onSelect,
		onRename,
		onDelete,
	} = useContext(TreeViewContext);

	const { mutate: createNode } = useCreateNode();

	const isEditing = renamingId === node.id;
	const isActive = activeNote?.id === node.id;
	const isOpen = expandedIds.has(node.id);
	const [tempTitle, setTempTitle] = useState(node.title);
	const isAborted = useRef(false);
	const editRef = useRef<HTMLDivElement>(null);

	// Setup Draggable logic
	const { attributes, listeners, setNodeRef, transform, isDragging } =
		useDraggable({
			id: node.id,
			data: node,
		});

	// Setup Droppable logic (Only if it's a folder!)
	const { setNodeRef: setDropRef, isOver } = useDroppable({
		id: node.id,
		disabled: !node.isFolder,
	});

	const style = transform
		? {
				transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
				zIndex: 999,
			}
		: undefined;

	useEffect(() => {
		if (!isEditing) {
			setTempTitle(node.title);
		}
	}, [node.title, isEditing]);

	const handleSave = useCallback(() => {
		setRenamingId(null);
		const trimmedTitle = tempTitle.trim();
		if (trimmedTitle === "") {
			setTempTitle(node.title);
			return;
		}
		if (trimmedTitle !== node.title) {
			onRename(node.id, trimmedTitle);
		}
	}, [tempTitle, node.title, onRename, setRenamingId, node.id]);

	useEffect(() => {
		if (!isEditing) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (editRef.current && !editRef.current.contains(event.target as Node)) {
				isAborted.current = false;
				handleSave();
			}
		};

		document.addEventListener("click", handleClickOutside, true);
		return () => {
			document.removeEventListener("click", handleClickOutside, true);
		};
	}, [isEditing, handleSave]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === "Tab") {
			e.preventDefault();
			isAborted.current = false;
			handleSave();
		}
		if (e.key === "Escape") {
			e.preventDefault();
			isAborted.current = true;
			setTempTitle(node.title);
			setRenamingId(null);
		}
	};

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (e.type === "mousedown") {
			if (node.isFolder) toggleExpanded(node.id);
		} else if (e.type === "click" && e.detail === 0) {
			if (node.isFolder) toggleExpanded(node.id);
		}
	};

	const handleNodeClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onSelect(node);
	};

	const handleCreateNote = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!isOpen) toggleExpanded(node.id);
		createNode(
			{
				title: "New Note.md",
				content: "",
				isFolder: false,
				parentId: node.id,
			},
			{
				onSuccess: (newNode) => {
					onSelect(newNode as FileNode);
					setRenamingId(newNode.id ?? null);
				},
			},
		);
	};

	const handleCreateFolder = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!isOpen) toggleExpanded(node.id);
		createNode(
			{
				title: "New Folder",
				content: null,
				isFolder: true,
				parentId: node.id,
			},
			{
				onSuccess: (newNode) => {
					onSelect(newNode as FileNode);
					setRenamingId(newNode.id ?? null);
				},
			},
		);
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`tree-node-container ${isDragging ? "dragging" : ""}`}
		>
			<div
				ref={setDropRef}
				className={`tree-node ${isActive ? "active" : ""} ${isOver ? "droppable-hover" : ""}`}
			>
				<DragHandle listeners={listeners || {}} attributes={attributes} />
				{node.isFolder && (
					<button
						type="button"
						className="toggle-icon"
						onMouseDown={handleToggle}
						onClick={handleToggle}
						aria-label={isOpen ? "Collapse" : "Expand"}
					>
						{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
					</button>
				)}

				{isEditing ? (
					<div ref={editRef} style={{ display: "contents" }}>
						<NodeEditInput
							node={node}
							tempTitle={tempTitle}
							setTempTitle={setTempTitle}
							handleKeyDown={handleKeyDown}
						/>
					</div>
				) : (
					<NodeStaticContent
						node={node}
						isActive={isActive}
						handleNodeClick={handleNodeClick}
						setRenamingId={setRenamingId}
					/>
				)}

				{!isEditing && (
					<span className="hover-actions">
						{node.isFolder && (
							<>
								<button
									type="button"
									title="New Note"
									onClick={handleCreateNote}
									className="action-button"
									aria-label="New Note"
								>
									<Plus size={12} />
								</button>
								<button
									type="button"
									title="New Folder"
									onClick={handleCreateFolder}
									className="action-button"
									aria-label="New Folder"
								>
									<FolderPlus size={12} />
								</button>
							</>
						)}
						<button
							type="button"
							title="Rename"
							onClick={(e) => {
								e.stopPropagation();
								setRenamingId(node.id);
							}}
							className="action-button"
							aria-label="Rename"
						>
							<Pencil size={12} />
						</button>
						<button
							type="button"
							title="Delete"
							onClick={(e) => {
								e.stopPropagation();
								onDelete(node.id);
							}}
							className="action-button delete-button"
							aria-label="Delete"
						>
							<Trash2 size={12} />
						</button>
					</span>
				)}
			</div>

			{node.isFolder && isOpen && node.children && (
				// biome-ignore lint/a11y/useSemanticElements: role group is standard for tree structures
				<div className="children-container" role="group">
					{node.children.map((child) => (
						<TreeNode key={child.id} node={child} />
					))}
				</div>
			)}
		</div>
	);
});

TreeNode.displayName = "TreeNode";
